import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { auth } from '../lib/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { getUserRole, ROLES } from '../lib/roles'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

const GOOGLE_TOKEN_KEY = 'google_calendar_token'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState(null)
  const [roleLoading, setRoleLoading] = useState(true)
  const [googleAccessToken, setGoogleAccessToken] = useState(null)
  const [googleTokenExpiry, setGoogleTokenExpiry] = useState(null)

  useEffect(() => {
    const stored = localStorage.getItem(GOOGLE_TOKEN_KEY)
    if (!stored) return
    try {
      const parsed = JSON.parse(stored)
      if (!parsed?.accessToken) {
        localStorage.removeItem(GOOGLE_TOKEN_KEY)
        return
      }
      if (parsed?.expiry && parsed.expiry <= Date.now()) {
        localStorage.removeItem(GOOGLE_TOKEN_KEY)
        return
      }
      setGoogleAccessToken(parsed.accessToken)
      setGoogleTokenExpiry(parsed.expiry || null)
    } catch (error) {
      console.warn('Failed to parse stored Google token', error)
      localStorage.removeItem(GOOGLE_TOKEN_KEY)
    }
  }, [])

  const updateGoogleToken = useCallback(({ accessToken, expiry }) => {
    if (!accessToken) {
      localStorage.removeItem(GOOGLE_TOKEN_KEY)
      setGoogleAccessToken(null)
      setGoogleTokenExpiry(null)
      return
    }
    const tokenData = {
      accessToken,
      expiry: expiry || null
    }
    localStorage.setItem(GOOGLE_TOKEN_KEY, JSON.stringify(tokenData))
    setGoogleAccessToken(accessToken)
    setGoogleTokenExpiry(expiry || null)
  }, [])

  const clearGoogleToken = useCallback(() => {
    localStorage.removeItem(GOOGLE_TOKEN_KEY)
    setGoogleAccessToken(null)
    setGoogleTokenExpiry(null)
  }, [])

  const refreshRole = useCallback(
    async (currentUser = auth.currentUser) => {
      if (!currentUser) {
        setRole(null)
        setRoleLoading(false)
        return
      }

      setRoleLoading(true)
      try {
        const token = await currentUser.getIdToken()
        const fetchedRole = await getUserRole(token)
        setRole(fetchedRole || ROLES.CLIENT)
      } catch (error) {
        console.error('Failed to fetch user role', error)
        setRole(ROLES.CLIENT)
      } finally {
        setRoleLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user)
      if (!user) {
        clearGoogleToken()
        setRole(null)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [clearGoogleToken])

  useEffect(() => {
    if (loading) return
    refreshRole(user)
  }, [loading, user, refreshRole])

  const value = {
    user,
    loading,
    role,
    roleLoading,
    googleAccessToken,
    googleTokenExpiry,
    updateGoogleToken,
    clearGoogleToken,
    refreshRole
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
} 